/**
 * Job Hunter — the frame every screen sits in: header, the candidate in use
 * (switchable), the module tabs and the "Run agents" control that starts a pass
 * now (all active agents, or the ones picked) and reports what it found.
 */
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Bot, Briefcase, Building2, Check, ChevronDown, LayoutDashboard, LoaderCircle, Play, Rss, ScanSearch, SquareKanban, Target, UserPlus, Users,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { refreshAgents, useJobHunter } from '@/lib/job-hunter/store';
import { agentHex } from '@/lib/job-hunter/format';
import { jobHunterService, type JhRun } from '@/services/job-hunter.service';
import { RouteSkeleton } from '@/components/PageSkeletons';
import { CandidateAvatar, JhSelect } from './ui';
import './job-hunter.css';

export const JH = {
  HOME: '/job-hunter',
  MATCHES: '/job-hunter/matches',
  PIPELINE: '/job-hunter/pipeline',
  AGENTS: '/job-hunter/agents',
  CANDIDATES: '/job-hunter/candidates',
  JOBS: '/job-hunter/jobs',
  EMPLOYERS: '/job-hunter/employers',
  SOURCES: '/job-hunter/sources',
  /** Old single-candidate page — redirects to the active candidate. */
  PROFILE: '/job-hunter/profile',
  NEW_CANDIDATE: '/job-hunter/candidates?new=1',
  candidate: (uid: string, tab?: CandidateTab) => `/job-hunter/candidates/${encodeURIComponent(uid)}${tab && tab !== 'profile' ? `?tab=${tab}` : ''}`,
};
export type CandidateTab = 'profile' | 'cvs' | 'portfolio' | 'alerts' | 'limits';

export type JhSection = 'Overview' | 'Matches' | 'Pipeline' | 'Agents' | 'Candidates' | 'Jobs' | 'Employers' | 'Sources';

const NAV = [
  { label: 'Overview', to: JH.HOME, Icon: LayoutDashboard, end: true },
  { label: 'Matches', to: JH.MATCHES, Icon: Target, end: false },
  { label: 'Pipeline', to: JH.PIPELINE, Icon: SquareKanban, end: false },
  { label: 'Agents', to: JH.AGENTS, Icon: Bot, end: false },
  { label: 'Candidates', to: JH.CANDIDATES, Icon: Users, end: false },
  { label: 'Jobs explorer', to: JH.JOBS, Icon: ScanSearch, end: false },
  { label: 'Employers', to: JH.EMPLOYERS, Icon: Building2, end: false },
  { label: 'Sources', to: JH.SOURCES, Icon: Rss, end: false },
];

const RUN_EVENT = 'jh:run-finished';
/** Pages refresh their data when a run started from here finishes. */
export function useRunFinished(cb: () => void) {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    const h = () => ref.current();
    window.addEventListener(RUN_EVENT, h);
    return () => window.removeEventListener(RUN_EVENT, h);
  }, []);
}

const summary = (r: JhRun) => {
  const parts = [`${r.sourcesPolled} source${r.sourcesPolled === 1 ? '' : 's'} polled`, `${r.jobsNew} new job${r.jobsNew === 1 ? '' : 's'}`, `${r.scored} scored`, `${r.proposed} new match${r.proposed === 1 ? '' : 'es'}`];
  return parts.join(' · ');
};

/** Starts a run and toasts its result when it finishes. One run at a time per workspace. */
export function useAgentRun() {
  const [running, setRunning] = useState(false);
  const timer = useRef<number | null>(null);
  const runUid = useRef<string | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const watch = useCallback(() => {
    timer.current = window.setTimeout(async () => {
      try {
        const { running: busy, runs } = await jobHunterService.runs(5);
        const mine = runs.find((r) => r.runUid === runUid.current);
        if (!busy && mine?.finishedAt) {
          setRunning(false);
          toast.success(`Run finished — ${summary(mine)}`, { duration: 6000 });
          if (mine.errorLog.length) toast.error(`${mine.errorLog.length} source/AI issue(s) — see Sources → Agent runs`, { duration: 6000 });
          void refreshAgents();
          window.dispatchEvent(new Event(RUN_EVENT));
          return;
        }
      } catch { /* toasted */ }
      watch();
    }, 4000);
  }, []);

  const start = useCallback(async (agentUids?: string[], label?: string) => {
    setRunning(true);
    try {
      const r = await jobHunterService.runNow(false, agentUids);
      runUid.current = r.runUid;
      toast(`${label ?? 'Agents'} started — polling sources and scoring new jobs…`, { icon: '🛰️' });
      watch();
    } catch { setRunning(false); }
  }, [watch]);

  return { running, start };
}

/** Main button runs every active agent; the menu runs the ones you tick (paused ones too). */
export function RunAgentButton({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const { agents } = useJobHunter();
  const { running, start } = useAgentRun();
  const [menu, setMenu] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const box = useRef<HTMLDivElement>(null);
  const active = (agents ?? []).filter((a) => a.active);

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menu]);

  const all = () => start(undefined, active.length === 1 ? active[0].name : `${active.length || 'All'} agents`);
  const runPicked = () => { setMenu(false); void start(picked, picked.length === 1 ? agents?.find((a) => a.agentUid === picked[0])?.name : `${picked.length} agents`); };

  return (
    <div ref={box} className="relative inline-flex">
      <Button size={size} onClick={all} disabled={running} className={agents?.length ? '!rounded-r-none' : undefined}
        iconLeft={running ? <LoaderCircle size={15} className="animate-spin" /> : <Play size={15} />}>
        {running ? 'Agents running…' : active.length > 1 ? `Run ${active.length} agents` : 'Run agents'}
      </Button>
      {!!agents?.length && (
        <Button size={size} disabled={running} className="!rounded-l-none border-l border-white/20 !px-2" aria-label="Pick agents to run" aria-expanded={menu} onClick={() => { setPicked(active.map((a) => a.agentUid)); setMenu((v) => !v); }}>
          <ChevronDown size={15} />
        </Button>
      )}
      {menu && agents && (
        <div className="absolute right-0 top-full z-40 mt-2 w-72 rounded-2xl border border-border bg-surface p-2 shadow-2xl">
          <div className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">Run these agents now</div>
          <div className="max-h-72 space-y-0.5 overflow-y-auto">
            {agents.map((a) => {
              const on = picked.includes(a.agentUid);
              return (
                <button key={a.agentUid} type="button" onClick={() => setPicked((p) => (on ? p.filter((x) => x !== a.agentUid) : [...p, a.agentUid]))}
                  className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left text-[13px] hover:bg-surface-alt">
                  <span className={clsx('grid h-4 w-4 shrink-0 place-items-center rounded border', on ? 'border-primary bg-primary text-on-primary' : 'border-border-strong')}>{on && <Check size={11} />}</span>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: agentHex(a.color) }} />
                  <span className="min-w-0 flex-1 truncate font-medium">{a.name}</span>
                  {!a.active && <span className="jh-chip !text-[10px]">Paused</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-border px-1 pt-2">
            <Link to={JH.AGENTS} className="text-xs font-semibold text-primary" onClick={() => setMenu(false)}>Manage agents</Link>
            <Button size="sm" disabled={!picked.length} onClick={runPicked} iconLeft={<Play size={13} />}>Run {picked.length || ''}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileSwitcher() {
  const { profiles, profileUid, setActive } = useJobHunter();
  const loc = useLocation();
  const nav = useNavigate();
  if (!profiles?.length) return null;
  const onCandidatePage = /^\/job-hunter\/candidates\/[^/]+/.test(loc.pathname);
  const pick = (uid: string) => {
    setActive(uid);
    if (onCandidatePage) nav(`${JH.candidate(uid)}${loc.search}`);
  };
  const current = profiles.find((p) => p.profileUid === profileUid) ?? profiles[0];
  if (profiles.length === 1) {
    return (
      <Link to={JH.candidate(current.profileUid)} className="hidden h-9 items-center gap-2 rounded-xl border border-border bg-surface pl-1 pr-3 text-xs font-semibold text-text-secondary transition hover:border-primary sm:inline-flex">
        <CandidateAvatar name={current.name} photoUrl={current.photo?.url} size={26} />{current.name}
      </Link>
    );
  }
  return (
    <JhSelect value={current.profileUid} onChange={pick} size="sm" className="w-48" ariaLabel="Candidate" searchPlaceholder="Search candidates"
      options={profiles.map((p) => ({ value: p.profileUid, label: p.name, description: p.active ? p.headline : 'Paused', icon: <CandidateAvatar name={p.name} photoUrl={p.photo?.url} size={20} />, keywords: p.headline }))} />
  );
}

function NeedsProfile() {
  return (
    <div className="mx-auto flex min-h-[52vh] max-w-2xl items-center py-6">
      <div className="jh-hero w-full text-center sm:p-10">
        <div className="mx-auto jh-brand !h-16 !w-16 !rounded-2xl"><Briefcase size={28} /></div>
        <h1 className="mt-5">Set up your job-hunting agents</h1>
        <p className="mx-auto mt-2 max-w-md text-[14px] leading-relaxed text-text-muted">
          Start with the candidate: work rights, facts and a CV. Then give them one or more agents — each with its own CV, roles and countries.
          The agents only ever use the candidate's facts — they never invent an answer.
        </p>
        <div className="mt-7 flex flex-col items-center gap-3">
          <Link to={JH.NEW_CANDIDATE}><Button iconLeft={<UserPlus size={15} />}>Add a candidate</Button></Link>
        </div>
      </div>
    </div>
  );
}

export function JobHunterShell({ section, description, actions, children, needsProfile = true }: {
  section: JhSection; description: string; actions?: ReactNode; children: ReactNode; needsProfile?: boolean;
}) {
  const { profiles, error, reload } = useJobHunter();
  const loading = profiles === null && !error;
  return (
    <div className="jh-module min-h-full">
      <Topbar title={`Job Hunter · ${section}`} description={description} breadcrumb={`Job Hunter / ${section}`} autoBack={false}
        back={<span className="jh-brand"><Briefcase size={19} /></span>}
        right={<div className="flex items-center gap-2"><ProfileSwitcher />{profiles?.length ? <RunAgentButton size="sm" /> : null}</div>} />
      <div className="jh-page">
        <nav className="jh-nav" aria-label="Job Hunter">
          {NAV.map(({ label, to, Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `jh-nav-link ${isActive ? 'is-active' : ''}`}><Icon size={15} />{label}</NavLink>
          ))}
        </nav>
        {/* Eight tabs fill the bar at laptop widths, so page actions get their own row instead of being clipped. */}
        {actions && <div className="-mt-2 mb-4 flex flex-wrap items-center justify-end gap-2">{actions}</div>}
        {error ? (
          <div className="jh-card jh-card-pad text-center">
            <div className="jh-card-title justify-center">Job Hunter could not load</div>
            <p className="jh-muted mt-1">Check your connection and try again.</p>
            <Button className="mt-4" variant="secondary" onClick={() => void reload()}>Try again</Button>
          </div>
        ) : loading ? <RouteSkeleton pathname="/job-hunter" />
          : needsProfile && !profiles?.length ? <NeedsProfile />
          : children}
      </div>
    </div>
  );
}
